import React, { useEffect, useMemo, useRef, useState } from 'react';

// ANT Design
import { TreeSelect, Typography } from 'antd';
import Tooltip from '../common/Tooltip';
import { navbarElementStyle } from '../../utils/styles';
import { searchCaseDiatricsInsensitive } from '../../utils/general';

// Tooltip AntD sur le libellé, mais seulement quand il est réellement tronqué
// (mesure native d'antd, pas un survol systématique) — Typography.Text avec
// `ellipsis.tooltip` ne l'affiche que si le texte dépasse effectivement.
const EllipsisLabel = ({ children }) => (
  <Typography.Text
    ellipsis={{ tooltip: { mouseLeaveDelay: 0 } }}
    style={{ flex: 1, minWidth: 0 }}
  >
    {children}
  </Typography.Text>
);

// Regroupe une liste plate d'options (sections / sections récursives, déjà
// filtrées et enrichies par SelectionBox) sous leurs dossiers (Proposals::Folder,
// arborescence par parent_id). Un dossier n'apparaît que s'il contient — direc-
// tement ou via un sous-dossier — au moins une option : sinon le picker se
// remplit de dossiers vides sans rien à y choisir.
const buildTreeData = (options, folders) => {
  const byParent = new Map();
  (folders || []).forEach((f) => {
    const key = f.parent_id ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(f);
  });
  const byFolder = new Map();
  const rootOptions = [];
  (options || []).forEach((opt) => {
    const key = opt.folder_id ?? null;
    if (key == null) {
      rootOptions.push(opt);
      return;
    }
    if (!byFolder.has(key)) byFolder.set(key, []);
    byFolder.get(key).push(opt);
  });

  // `title` porte le rendu riche (icône récursive + libellé + œil d'aperçu),
  // `label` reste la chaîne brute : c'est elle qu'affiche le champ une fois la
  // section choisie (treeNodeLabelProp) et sur laquelle porte la recherche.
  const optionNode = (opt) => {
    const node = {
      key: `s-${opt.value}`,
      value: opt.value,
      label: opt.label,
      disabled: opt.disabled,
      isLeaf: true,
      title: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', minWidth: 0 }}>
          {opt.icon}
          <EllipsisLabel>{opt.label}</EllipsisLabel>
          {opt.extra}
        </span>
      ),
    };
    // `opt.title` porte la raison de désactivation (ex. imbrication récursive
    // invalide) — gardée en tooltip AntD plutôt qu'en attribut HTML natif.
    if (!opt.title) return node;
    return { ...node, title: <Tooltip title={opt.title} mouseLeaveDelay={0}>{node.title}</Tooltip> };
  };

  const buildFolder = (folderId) => {
    const childFolders = (byParent.get(folderId) || [])
      .map((f) => buildFolder(f.id))
      .filter(Boolean);
    const childOptions = (byFolder.get(folderId) || []).map(optionNode);
    if (childFolders.length === 0 && childOptions.length === 0) return null;
    const folder = folders.find((f) => f.id === folderId);
    return {
      key: `f-${folderId}`,
      value: `f-${folderId}`,
      label: folder?.name ?? '',
      // Nœud JSX plutôt qu'une chaîne brute : rc-tree pose sinon sa propre
      // valeur en attribut HTML `title` sur la ligne (infobulle native au survol).
      title: <EllipsisLabel>{folder?.name}</EllipsisLabel>,
      selectable: false,
      // Fond distinctif permanent (teinte de survol antd, posée en dur ici) :
      // un dossier n'a par défaut aucun style de survol (nœud non sélectionnable,
      // cf. antd Tree), donc rien ne le distinguait visuellement d'une section.
      className: 'section-tree-folder-node',
      children: [...childFolders, ...childOptions],
    };
  };

  const folderNodes = (byParent.get(null) || []).map((f) => buildFolder(f.id)).filter(Boolean);
  return [...folderNodes, ...rootOptions.map(optionNode)];
};

const filterTreeNode = (input, node) =>
  searchCaseDiatricsInsensitive(input, node.label);

// Pendant de NavbarSelector pour les sections : mêmes réglages de popup/style,
// mais les options sont regroupées par dossier au lieu d'une liste plate.
const SectionTreeSelector = ({
  width,
  disabled,
  suffixIcon,
  onSelect,
  selectedValue,
  options,
  folders,
  placeholder,
  isOpen,
  onToggleOpen,
  popupClassName,
  popupMatchSelectWidth,
  dropdownStyle,
  dropdownRender,
  focusNonce = null,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const selectRef = useRef(null);
  // Chaque nouvelle valeur place le curseur dans la recherche (cf. SelectionBox,
  // « Choisir le contenu ») : on tape directement le nom de la section.
  useEffect(() => {
    if (focusNonce == null) return;
    selectRef.current?.focus();
  }, [focusNonce]);
  const dropdownOpen = isOpen !== undefined ? isOpen : internalOpen;
  const setDropdownOpen = onToggleOpen || setInternalOpen;

  const treeData = useMemo(() => buildTreeData(options, folders), [options, folders]);

  return (
    <TreeSelect
      ref={selectRef}
      className='navbar-selector'
      style={{ ...navbarElementStyle, width: width || '120px' }}
      value={selectedValue}
      disabled={disabled}
      open={dropdownOpen}
      onOpenChange={setDropdownOpen}
      suffixIcon={suffixIcon}
      placeholder={placeholder}
      treeNodeLabelProp='label'
      showSearch
      filterTreeNode={filterTreeNode}
      treeDefaultExpandAll
      treeData={treeData}
      popupMatchSelectWidth={popupMatchSelectWidth}
      popupClassName={popupClassName}
      {...(dropdownStyle ? { styles: { popup: { root: dropdownStyle } } } : {})}
      onChange={(value) => {
        onSelect(value);
        setDropdownOpen(false);
      }}
      dropdownRender={dropdownRender}
      onInputKeyDown={(e) => { e.stopPropagation(); }}
    />
  );
};

export default React.memo(SectionTreeSelector);
